import { autoUpdate, flip, offset, shift, useDismiss, useFloating, useInteractions } from '@floating-ui/react';
import pullAllBy from 'lodash/pullAllBy';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { getAccountOwnerName } from 'utils';
import { PersonCoreData } from 'views/person/person-queries';
import { Card, HorizontalLayout, IconChevron, SearchInput, Spinner, Text, VerticalLayout } from 'widgets';

const LIMIT = 100;
const personNameCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

export const sortPersonProfilesAlphabetically = (profiles: PersonCoreData[]) =>
    [...profiles].sort((left, right) => {
        const leftName = getAccountOwnerName(left);
        const rightName = getAccountOwnerName(right);
        const byName = personNameCollator.compare(leftName, rightName);

        if (byName !== 0) {
            return byName;
        }

        return personNameCollator.compare(left.personOrgGUID, right.personOrgGUID);
    });

export const usePersonSelector = ({
    profiles,
    isLoading = false,
    excludeItems,
    placeholder = 'Select person',
}: {
    profiles: PersonCoreData[] | undefined;
    isLoading?: boolean;
    excludeItems?: Pick<PersonCoreData, 'personOrgGUID'>[];
    placeholder?: string;
}) => {
    const currentProfiles = profiles;
    const [searchTerm, setSearchTerm] = useState('');
    const [open, setOpen] = useState(false);

    const [selectedPersonProfile, setSelectedPersonProfile] = useState<any>();

    const { x, y, refs, strategy, context } = useFloating({
        placement: 'bottom-start',
        strategy: 'fixed',
        open,
        onOpenChange: setOpen,
        middleware: [offset(8), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    });
    useInteractions([useDismiss(context)]);

    const reset = useCallback(() => {
        setSelectedPersonProfile(undefined);
        setOpen(false);
        setSearchTerm('');
    }, []);

    useEffect(() => {
        if (!open) {
            setSearchTerm('');
        }
    }, [open, searchTerm]);

    const filteredPersonProfiles = useMemo(() => {
        if (!currentProfiles) {
            return [];
        }

        let filteredProfiles = currentProfiles;

        if (excludeItems) {
            filteredProfiles = pullAllBy([...filteredProfiles], excludeItems, 'personOrgGUID');
        }

        filteredProfiles = sortPersonProfilesAlphabetically(filteredProfiles);

        if (!searchTerm) {
            return filteredProfiles.slice(0, LIMIT);
        }
        return filteredProfiles
            .filter((profile) => {
                return [getAccountOwnerName(profile), profile.personOrgGUID, profile.email].some(
                    (text) => text && text.toLowerCase().includes(searchTerm.toLowerCase()),
                );
            })
            .slice(0, LIMIT);
    }, [excludeItems, currentProfiles, searchTerm]);

    const PersonSelector = useCallback(
        () => (
            <>
                <VerticalLayout>
                    <Wrapper ref={refs.setReference}>
                        {open ? (
                            <SearchInput
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(ev) => setSearchTerm(ev.target.value)}
                                autoFocus
                            />
                        ) : (
                            <StyledSelect
                                $apart
                                $rowCenter
                                onClick={() => !isLoading && setOpen(true)}
                                $disabled={isLoading}
                            >
                                {selectedPersonProfile ? (
                                    <Text $truncated>
                                        {getAccountOwnerName(selectedPersonProfile)}
                                        {selectedPersonProfile.email && (
                                            <Text $themeColor="subtle" helper>
                                                &nbsp;[{selectedPersonProfile.email}]
                                            </Text>
                                        )}
                                    </Text>
                                ) : (
                                    <Text $truncated>{placeholder}</Text>
                                )}
                                {isLoading ? <Spinner size={16} /> : <StyledIconChevron size="micro" />}
                            </StyledSelect>
                        )}
                    </Wrapper>
                    <Text helper $themeColor="subtle">
                        Type to search for name, email or GUID. Showing first {LIMIT} results.
                    </Text>
                </VerticalLayout>

                {open && (
                    <Dropdown
                        ref={refs.setFloating}
                        style={{
                            position: strategy,
                            top: y ?? 0,
                            left: x ?? 0,
                        }}
                    >
                        {filteredPersonProfiles && filteredPersonProfiles.length > 0 ? (
                            filteredPersonProfiles?.map((personProfile) => (
                                <ListItem
                                    onClick={() => {
                                        setSelectedPersonProfile(personProfile);
                                        setOpen(false);
                                    }}
                                    key={personProfile.personOrgGUID}
                                    $padding="4px 8px"
                                >
                                    <Text $truncated>
                                        {getAccountOwnerName(personProfile)}
                                        {personProfile.email && (
                                            <Text $themeColor="subtle" helper>
                                                &nbsp;[{personProfile.email}]
                                            </Text>
                                        )}
                                    </Text>
                                </ListItem>
                            ))
                        ) : (
                            <VerticalLayout $padding="16px">
                                <Text $italic $themeColor="subtle">
                                    No results found
                                </Text>
                            </VerticalLayout>
                        )}
                    </Dropdown>
                )}
            </>
        ),
        [
            filteredPersonProfiles,
            isLoading,
            open,
            placeholder,
            refs.setFloating,
            refs.setReference,
            searchTerm,
            selectedPersonProfile,
            strategy,
            x,
            y,
        ],
    );

    return {
        PersonSelector,
        selectedPersonProfile,
        reset,
    };
};

const ListItem = styled(HorizontalLayout)`
    cursor: pointer;
    &:hover {
        background: ${({ theme }) => theme.container.secondaryBackground};
    }
`;

const StyledIconChevron = styled(IconChevron)`
    transform: rotate(90deg);
`;

const StyledSelect = styled(HorizontalLayout)<{ $disabled: boolean }>`
    height: 35px;
    padding: 0 8px;
    text-overflow: ellipsis;

    border-radius: 3px;
    border: 1px solid ${({ theme }) => theme.border.dark};
    cursor: pointer;

    ${({ $disabled, theme }) =>
        $disabled &&
        `
        pointer-events: none;
        background: ${theme.intent.disabled};
        `};
`;

const Dropdown = styled(Card)`
    overflow-y: auto;
    max-height: 300px;
    width: 500px;
    box-shadow: 0px 0px 10px 2px ${({ theme }) => theme.border.light};
    z-index: 5;
    padding: 8px 0;
    display: flex;
    flex-direction: column;
`;

const Wrapper = styled.div`
    overflow: hidden;
`;
